import api from '@/api'
import store from 'store'
import cookies from 'js-cookie'

import ShortUniqueId from 'short-unique-id'
const uid = new ShortUniqueId({ length: 16 })

// export interface Notification  {
//   id: string
//   created_at: number
//   message: string
//   link: string
//   level: string
//   read: Boolean
//   fromServer: Boolean
// }

const LEVELS = {
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  URGENT: 'urgent'
}

const NOTIFICATIONS_API_PAGINATION_DATA = {
  total_records: 0,
  total_not_read_records: 0,
  total_pages: 1,
  page: 1,
  limit: 100
}

export default {
  state: {
    notifications: [],
    notificationsApiPaginationData: NOTIFICATIONS_API_PAGINATION_DATA,
    lastNotificationAPIId: 0,
    lastNotificationAPICheck: false
  },
  mutations: {
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = []
      state.notificationsApiPaginationData = NOTIFICATIONS_API_PAGINATION_DATA
      state.lastNotificationAPIId = 0
      state.lastNotificationAPICheck = false
    },
    UPSERT_NOTIFICATION(state, notification) {
      let existing = state.notifications.find((n) => n.id == notification.id)
      if (existing) Object.assign(existing, notification)
      else state.notifications.push(notification)
    },
    REMOVE_NOTIFICATION(state, id) {
      const notificationItem = state.notifications.find((n) => n.id === id)
      state.notifications = state.notifications.filter((n) => n.id !== id)
      if (notificationItem?.data?.type === 'api') {
        state.notificationsApiPaginationData.total_not_read_records =
          state.notificationsApiPaginationData.total_not_read_records > 1 ? state.notificationsApiPaginationData.total_not_read_records - 1 : 0
      }
    },
    SET_NOTIFICATIONS_API_PAGINATION_DATA(state, data) {
      state.notificationsApiPaginationData = data
    },
    SET_LAST_NOTIFICATION_API_ID(state, id) {
      state.lastNotificationAPIId = id
    },
    SET_LAST_NOTIFICATION_API_CHECK(state, check = false) {
      state.lastNotificationAPICheck = check
    }
  },
  actions: {
    addNotification({ commit, dispatch }, { id, created_at, message, level = LEVELS.INFO, link, data, read = false }) {
      if (message == null) throw new Error('No message passed')
      id = id ?? crypto.randomUUID()
      created_at = created_at ?? Date.now()
      let notification = { id, created_at, message, level, read, link, data }
      commit('UPSERT_NOTIFICATION', notification)
    },
    removeNotification({ commit, dispatch }, id) {
      commit('REMOVE_NOTIFICATION', id)
    },
    readNotification({ commit, dispatch }, id) {
      commit('UPSERT_NOTIFICATION', { id, read: true })
    },
    clearNotifications({ commit }) {
      commit('CLEAR_NOTIFICATIONS')
    },
    setNotificationsApiPaginationData({ commit }, data) {
      let notificationsApiPaginationData = {}
      Object.keys(NOTIFICATIONS_API_PAGINATION_DATA).forEach((key) => {
        notificationsApiPaginationData[key] = data[key] ?? notificationsApiPaginationData[key]
      })
      commit('SET_NOTIFICATIONS_API_PAGINATION_DATA', notificationsApiPaginationData)
    },
    setLastNotificationApiId({ commit }, id) {
      commit('SET_LAST_NOTIFICATION_API_ID', id)
    },
    setLastNotificationApiCheck({ commit }, check = false) {
      commit('SET_LAST_NOTIFICATION_API_CHECK', check)
    },
    async checkToLoadApiNotifications({ state, dispatch, rootState }) {
      const currentStation = rootState?.station?.current?.station || ''
      const notificationEnabledStationsList = rootState?.station?.notificationEnabledStationsList || []
      if (!notificationEnabledStationsList.includes(currentStation)) {
        return
      }

      // check last API notification id - if not match then call refresh loadApiNotifications
      if (state.lastNotificationAPICheck) {
        return
      }
      await dispatch('setLastNotificationApiCheck', true)
      const resp = await api.newsdesk.checkApiNotifications().finally(async () => {
        await dispatch('setLastNotificationApiCheck', false)
      })
      const lastAPINotificationId = resp?.data || 0
      if (lastAPINotificationId !== state.lastNotificationAPIId) {
        await dispatch('setLastNotificationApiId', lastAPINotificationId)
        await dispatch('loadApiNotifications', { isRefresh: true })
      }
    },
    async loadApiNotifications({ dispatch, state, rootState }, data = {}) {
      const currentStation = rootState?.station?.current?.station || ''
      const notificationEnabledStationsList = rootState?.station?.notificationEnabledStationsList || []
      if (!notificationEnabledStationsList.includes(currentStation)) {
        return
      }
      const { params = {}, isRefresh = false } = data
      const notificationsData = await dispatch('getApiNotifications', params)
      if (isRefresh) {
        notificationsData.page = state.notificationsApiPaginationData.page
        dispatch('setNotificationsApiPaginationData', notificationsData)
      } else {
        dispatch('setNotificationsApiPaginationData', notificationsData)
      }
      if (notificationsData.records && notificationsData.records.length > 0) {
        notificationsData.records.forEach((notification) => {
          let link = null
          if (notification?.record_type && notification?.record_id) {
            if (notification?.record_type === 'story') {
              link = `/stories?storyId=${notification.record_id}&station=${notification.station}`
              if (notification?.params?.storyView) {
                link = `${link}&storyView=${notification.params.storyView}`
              }
            }
          }
          if (notification?.record_type === 'selected_media_zip') {
            link = notification?.params?.link || ''
          }
          const notificationData = {
            id: notification?.id || 0,
            data: {
              type: `api`,
              title: notification?.heading || null,
              datetime: notification?.datetime ?? null,
              link: link,
              record_type: notification?.record_type ?? ''
            },
            message: notification?.message || '',
            level: 'info',
            read: notification?.status ? false : true,
            created_at: notification.date ? Date.parse(notification.date) : null
          }
          dispatch('addNotification', notificationData)
        })
      }
    },
    async getApiNotifications({ commit, dispatch }, params) {
      const resp = await api.newsdesk.getApiNotifications(params)
      return resp.data || []
    },
    async readApiNotification({ commit, dispatch }, id) {
      const resp = await api.newsdesk.readApiNotification(id)
    },
    async removeApiNotification({ commit, dispatch }, id) {
      const resp = await api.newsdesk.removeApiNotification(id)
    },
    async removeApiNotifications() {
      const resp = await api.newsdesk.removeApiNotifications()
    }
  },
  getters: {
    notifications(state) {
      return state.notifications.sort((a, b) => b.created_at - a.created_at)
    },
    notificationsApiPaginationData(state) {
      return state.notificationsApiPaginationData
    }
  }
}
